import { isDateOnlyYMD } from '../../../../../../../utils/dates';
import { StatusType } from '../../../../../../orders/order.types';
import { expandLegacyPreOrderFilter } from '../../../../../../products/utils/availability.utils';
import {
  PRODUCT_CLASSIFICATION_PREFIX,
  SALES_TARGET_CUSTOMER_CLASSIFICATION_PREFIX,
  SALES_TARGET_PRODUCT_CLASSIFICATION_PREFIX,
} from '../SalesTargetReportsFilter/salesTargetReportsFilter.utils';

export const REPORT_CLASSIFICATIONS_PREFIX = 'classificationPrefix';
export const REPORT_CUSTOMER_CLASSIFICATIONS_PREFIX = 'customerClassificationPrefix';
export const REPORTS_CUSTOMER_FILTER_KEYS = ['customersIdsByData', 'customerIdsByCode'];
export const ORDER_CONDITION_CLASSIFICATIONS_PREFIX = 'orderConditionClassificationsPrefix';

export const REPORTS_FILTERS_KEYS = {
  orderIds: 'orderIds',
  createdAtEndDate: 'createdAtEndDate',
  createdAtStartDate: 'createdAtStartDate',
  salesRepresentative: 'salesRepresentativeIds',
  orderSeller: 'sellerIds',
  orderStatus: 'status',
  customerIds: 'customerIds',
  orderCollections: 'orderCollectionsIds',
  [REPORTS_CUSTOMER_FILTER_KEYS[0]]: REPORTS_CUSTOMER_FILTER_KEYS[0],
  [REPORTS_CUSTOMER_FILTER_KEYS[1]]: REPORTS_CUSTOMER_FILTER_KEYS[1],
  customerSalesRepresentative: 'customerSalesRepresentativeIds',
  customerCitiesIds: 'customerCitiesIds',
  customerStates: 'customerStates',
  customerStatus: 'customerStatus',
  products: 'productIds',
  colorReferencesIds: 'colorReferencesIds',
  productColor: 'productColorIds',
  deliveryWindowsIds: 'deliveryWindowsIds',
  deliveryWindowStartDate: 'deliveryWindowStartDate',
  deliveryWindowEndDate: 'deliveryWindowEndDate',
  deliveryWindowDateShortcutStep: 'deliveryWindowDateShortcutStep',
  deliveryWindowDateShortcut: 'deliveryWindowDateShortcut',
  availability: 'availability',
  productCategories: 'categoriesIds',
  productCollections: 'productCollectionsIds',
  classifications: 'classificationsIds',
  consultants: 'consultantIds',
  customerClassificationIds: 'customerClassificationIds',
  customerGroupIds: 'customerGroupIds',
  customerSynchronizedStatus: 'customerSynchronizedStatus',
  dateShortcutStep: 'shortcutStep',
  dateShortcut: 'dateShortcut',
  orderConditionClassificationIds: 'orderConditionClassificationIds',
  productClassificationIds: 'productClassificationIds',
  salesTargetProductClassificationsIds: 'salesTargetProductClassificationsIds',
  salesTargetCustomerClassificationsIds: 'salesTargetCustomerClassificationsIds',
} as const;

export const REPORTS_PERIOD_FILTER_KEYS = {
  orderCollections: 'orderCollectionsPeriodIds',
  productCollections: 'productCollectionsPeriodIds',
  createdAtEndDate: 'createdAtEndDatePeriodFilter',
  createdAtStartDate: 'createdAtStartDatePeriodFilter',
};

export const systemOrderStatusDefaultValues = [
  StatusType.APPROVED,
  StatusType.SENT,
  StatusType.IN_REVISION,
  StatusType.ON_APPROVAL,
];

export const formatPeriodFilters = (values: Record<string, string>) => {
  const createdAtStartDate = values[REPORTS_PERIOD_FILTER_KEYS.createdAtStartDate];
  const createdAtEndDate = values[REPORTS_PERIOD_FILTER_KEYS.createdAtEndDate];
  const orderCollections = values[REPORTS_PERIOD_FILTER_KEYS.orderCollections];
  const productCollections = values[REPORTS_PERIOD_FILTER_KEYS.productCollections];

  delete values[REPORTS_PERIOD_FILTER_KEYS.createdAtStartDate];
  delete values[REPORTS_PERIOD_FILTER_KEYS.createdAtEndDate];

  delete values[REPORTS_PERIOD_FILTER_KEYS.orderCollections];
  delete values[REPORTS_PERIOD_FILTER_KEYS.productCollections];

  const result: Record<string, string | number | Date> = {
    [REPORTS_FILTERS_KEYS.orderCollections]: orderCollections,
    [REPORTS_FILTERS_KEYS.productCollections]: productCollections,
    [REPORTS_FILTERS_KEYS.createdAtStartDate]: createdAtStartDate,
    [REPORTS_FILTERS_KEYS.createdAtEndDate]: createdAtEndDate,
  };

  const dateKeys: string[] = [
    REPORTS_FILTERS_KEYS.createdAtStartDate,
    REPORTS_FILTERS_KEYS.createdAtEndDate,
  ];
  const filteredResult = Object.entries(result).filter(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return false;
    }
    return !dateKeys.includes(key) || typeof value !== 'string' || isDateOnlyYMD(value);
  });
  return Object.fromEntries(filteredResult);
};

export const formatSalesAndInvoicingReportsFilters = (values: Record<string, any> = {}) => {
  const cloneValues = { ...values };
  const classificationIds: string[] = [];
  const customerClassificationsIds: string[] = [];
  const orderConditionClassificationIds: string[] = [];
  const productClassificationsIds: string[] = [];
  const salesTargetProductClassificationsIds: string[] = [];
  const salesTargetCustomerClassificationsIds: string[] = [];
  const periodFilters = formatPeriodFilters(cloneValues);

  const valuesKey = Object.keys(cloneValues);
  const classificationsKeys = valuesKey.filter((key) =>
    key.startsWith(REPORT_CLASSIFICATIONS_PREFIX),
  );
  classificationsKeys.forEach((key) => {
    const ids = cloneValues[key];
    classificationIds.push(ids);
    delete cloneValues[key];
  });

  const orderConditionClassificationKeys = valuesKey.filter((key) =>
    key.startsWith(ORDER_CONDITION_CLASSIFICATIONS_PREFIX),
  );
  orderConditionClassificationKeys.forEach((key) => {
    const ids = cloneValues[key];
    orderConditionClassificationIds.push(ids);
    delete cloneValues[key];
  });

  const customerclassificationsKeys = valuesKey.filter((key) =>
    key.startsWith(REPORT_CUSTOMER_CLASSIFICATIONS_PREFIX),
  );
  customerclassificationsKeys.forEach((key) => {
    const ids = cloneValues[key];
    customerClassificationsIds.push(ids);
    delete cloneValues[key];
  });

  const salesTargetProductClassificationsKeys = valuesKey.filter((key) =>
    key.startsWith(SALES_TARGET_PRODUCT_CLASSIFICATION_PREFIX),
  );
  salesTargetProductClassificationsKeys.forEach((key) => {
    const ids = cloneValues[key];
    salesTargetProductClassificationsIds.push(ids);
    delete cloneValues[key];
  });

  const salesTargetCustomerClassificationsKeys = valuesKey.filter((key) =>
    key.startsWith(SALES_TARGET_CUSTOMER_CLASSIFICATION_PREFIX),
  );
  salesTargetCustomerClassificationsKeys.forEach((key) => {
    const ids = cloneValues[key];
    salesTargetCustomerClassificationsIds.push(ids);
    delete cloneValues[key];
  });

  const customerIds: string[] = [];
  REPORTS_CUSTOMER_FILTER_KEYS.forEach((key) => {
    const ids = cloneValues[key];
    delete cloneValues[key];
    if (ids) {
      const idsInArray = Array.isArray(ids) ? ids : [ids];
      customerIds.push(...idsInArray);
    }
  });

  const productClassificationsKeys = valuesKey.filter((key) =>
    key.startsWith(PRODUCT_CLASSIFICATION_PREFIX),
  );
  productClassificationsKeys.forEach((key) => {
    const ids = cloneValues[key];
    productClassificationsIds.push(ids);
    delete cloneValues[key];
  });

  const availability = expandLegacyPreOrderFilter(cloneValues[REPORTS_FILTERS_KEYS.availability]);
  const productAvailability = expandLegacyPreOrderFilter(cloneValues.productAvailability);

  return {
    ...cloneValues,
    ...periodFilters,
    ...(availability && { [REPORTS_FILTERS_KEYS.availability]: availability }),
    ...(productAvailability && { productAvailability }),
    [REPORTS_FILTERS_KEYS.customerIds]: customerIds,
    [REPORTS_FILTERS_KEYS.classifications]: classificationIds.flat(2),
    [REPORTS_FILTERS_KEYS.customerClassificationIds]: customerClassificationsIds.flat(2),
    [REPORTS_FILTERS_KEYS.orderConditionClassificationIds]: orderConditionClassificationIds.flat(2),
    [REPORTS_FILTERS_KEYS.productClassificationIds]: productClassificationsIds.flat(2),
    [REPORTS_FILTERS_KEYS.salesTargetProductClassificationsIds]:
      salesTargetProductClassificationsIds.flat(2),
    [REPORTS_FILTERS_KEYS.salesTargetCustomerClassificationsIds]:
      salesTargetCustomerClassificationsIds.flat(2),
  };
};
